import { MuiTelInput } from 'mui-tel-input'
import SearchIcon from '@mui/icons-material/Search'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectDerivedIncompleteTeacherEmploymentSetting,
    selectTeacherWorkingHistory,
} from '../../../../redux/teacherSlice'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    Typography,
} from '@mui/material'
import { RegModalWrapper } from '../ModalContent'
import styles from '../index.module.scss'
import { PrimaryButton } from '../../../../components/Button'
import {
    TeacherWorkingHistoryEntry,
    teacherWorkingHistoryEntrySchema,
    workingHistoryEmploymentTypeOptions,
} from '../../../../firebase/types-teacher'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DialogAppBar } from '../../../../components/Dialog/AppBar'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import { UserRegSx } from '../../../../components/TextField'
import FormSelect from '../../../../components/FormComponents/FormSelect'
import {
    GoogleMapsTextFieldV2,
    PlaceType,
} from '../../../../components/AutoComplete'
import { Fragment, useState } from 'react'
import FormDatePicker_v2 from '../../../../components/FormComponents/FormDatePicker_v2'
import z from 'zod'
import { DayjsSchema } from '../../../../schemas/helper'
import dayjs from '../../../../helpers/dayjs'
import { TEACHER_API } from '../../../../firebase/teacherApi'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { DEFAULT_ERR_MSG } from '../../../../helpers/errorMsg'
import { useMutation } from '@tanstack/react-query'
import { getLocationSafe } from '../../MyAccountPage/helper'
import { logger } from '../../../../helpers/logger'
import { selectUserUid } from '../../../../redux/userSlice'

const formSchema = teacherWorkingHistoryEntrySchema
    .extend({
        startDate: DayjsSchema,
        endDate: DayjsSchema.optional(),
        supervisorEmail: z.string('Incomplete*').or(z.literal('')),
        currentInRole: z.boolean().default(false),
    })
    .check((ctx) => {
        if (
            !ctx.value.currentInRole &&
            !!ctx.value.endDate &&
            !!ctx.value.startDate &&
            ctx.value.endDate.isBefore(ctx.value.startDate)
        ) {
            ctx.issues.push({
                code: 'custom',
                message: 'Should be after start date',
                input: ctx.value.endDate,
                path: ['endDate'],
            })
        }
        if (
            !ctx.value.currentInRole &&
            (!ctx.value.endDate || !ctx.value.endDate.isValid())
        ) {
            ctx.issues.push({
                code: 'custom',
                message: 'Incomplete*',
                input: ctx.value.endDate,
                path: ['endDate'],
            })
        }
    })
type FormType = z.infer<typeof formSchema>

type SelectHistory =
    | { id: string; data: TeacherWorkingHistoryEntry }
    | { id: undefined; data?: never }

const WorkingHistoryComponent = () => {
    const uid = useSelector(selectUserUid)
    const data = useSelector(selectTeacherWorkingHistory)

    const setting = useSelector(selectDerivedIncompleteTeacherEmploymentSetting)

    const [entry, setEntry] = useState<SelectHistory>()

    const dispatch = useDispatch()
    const { isPending, variables, mutateAsync } = useMutation({
        mutationFn: async (data: {
            id: string | undefined
            data: TeacherWorkingHistoryEntry
        }) => {
            try {
                if (typeof data.id !== 'string') {
                    await TEACHER_API.onboarding.createWorkingHistory(data.data)
                } else {
                    await TEACHER_API.onboarding.updateWorkingHistory(
                        data.id,
                        data.data
                    )
                }
            } catch (e) {
                dispatch(setErrorMsg(DEFAULT_ERR_MSG))
            }
        },
        onSuccess: () => {
            setEntry(undefined)
        },
    })

    const [open, setOpen] = useState(false)

    const handleOpen = (e: SelectHistory) => {
        setEntry(e)
        setOpen(true)
    }

    const markNoRelevantExperience = async () => {
        try {
            if (typeof uid !== 'string') throw new Error('Uid not defined')
            await TEACHER_API.onboarding.markNoWorkingHistory(uid)
        } catch (error) {
            dispatch(setErrorMsg(DEFAULT_ERR_MSG))
        }
    }

    if (data.status !== 'success') {
        return <LoadingSpinner id="working-history-data" />
    }

    return (
        <>
            {entry ? (
                <EditModal
                    open={open}
                    entry={entry}
                    onClose={() => {
                        setOpen(false)
                        setTimeout(() => setEntry(undefined), 300)
                    }}
                    mutateAsync={mutateAsync}
                />
            ) : null}
            <RegModalWrapper
                title="Working Rights"
                handleUpdate={() => {}}
                state={data.data.state}
                isAllComplete={
                    data.data.state === 'submitted'
                    // Object.entries(data.data.entries || {}).length > 0
                }
            >
                <Stack gap={5} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <Typography variant="h5">
                            Working History with Children
                        </Typography>
                        <Typography>
                            Add your relevant experience working with children
                            and young people. We recommend including at least
                            two roles, or any roles worked within the last 12
                            months. Focus on your most recent and longest-term
                            experience. Short-term placements or roles of fewer
                            than 5 days do not need to be included.
                        </Typography>
                    </Stack>

                    <Stack>
                        {Object.entries(data.data.entries || {}).map(
                            (el, i) => (
                                <Fragment key={`workingHistoryCard_${i}`}>
                                    <WorkingHistoryCard
                                        id={el[0]}
                                        data={el[1]}
                                        setEntry={handleOpen}
                                    />
                                </Fragment>
                            )
                        )}
                        {isPending && typeof variables.id !== 'string' ? (
                            <Fragment key={`workingHistoryCard_pending`}>
                                <WorkingHistoryCard
                                    id={''}
                                    data={variables.data}
                                    setEntry={() => {}}
                                />
                            </Fragment>
                        ) : null}
                        {data.data.entryCount === 0 &&
                        data.data.state === 'submitted' ? (
                            <Stack
                                sx={{
                                    boxShadow: `inset 0 -1px 0 ${styles.neutrals100}`,
                                    p: 2,
                                }}
                                direction={'row'}
                                alignItems={'center'}
                            >
                                <Stack flex={1}>
                                    <Typography variant="ps">
                                        No experience
                                    </Typography>
                                    <Typography
                                        variant="ps"
                                        color={styles.neutrals500}
                                    >
                                        I have not worked with children.
                                    </Typography>
                                </Stack>
                            </Stack>
                        ) : null}
                    </Stack>

                    <PrimaryButton
                        sx={{ width: 132 }}
                        onClick={() => handleOpen({ id: undefined })}
                    >
                        Add experience
                    </PrimaryButton>
                    {data.data.entryCount === 0 &&
                    data.data.state === 'incomplete' &&
                    setting === 'supportStaff' ? (
                        <Button
                            color="neutrals500"
                            // sx={{ mt: 'auto' }}
                            onClick={markNoRelevantExperience}
                        >
                            I don't have any relevant experience working with
                            children
                        </Button>
                    ) : null}
                </Stack>
            </RegModalWrapper>
        </>
    )
}

const WorkingHistoryCard = ({
    id,
    data,
    setEntry,
}: {
    id: string
    data: TeacherWorkingHistoryEntry
    setEntry: (e: SelectHistory) => void
}) => {
    const dispatch = useDispatch()

    const { isPending, mutateAsync: remove } = useMutation({
        mutationFn: async () => {
            try {
                await TEACHER_API.onboarding.removeWorkingHistory(id)
            } catch (e) {
                dispatch(setErrorMsg(DEFAULT_ERR_MSG))
            }
        },
    })

    const [confirm, setConfirm] = useState(false)

    if (isPending) {
        return null
    }

    const start = dayjs(data.startDate, 'MM-YYYY')
    const end = data.endDate ? dayjs(data.endDate, 'MM-YYYY') : null

    return (
        <>
            <Dialog open={confirm}>
                <DialogContent>
                    <Stack gap={1}>
                        <Typography variant="h6">Delete Experience</Typography>
                        <Typography>
                            Are you sure you want to delete this experience from
                            your working history with children?
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(false)}>Back</Button>
                    <Button
                        color="destructiveRed500"
                        onClick={() => {
                            setConfirm(false)
                            remove()
                        }}
                    >
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Stack
                role="button"
                sx={{
                    cursor: 'pointer',
                    boxShadow: `inset 0 -1px 0 ${styles.neutrals100}`,
                    p: 2,
                }}
                direction={'row'}
                alignItems={'center'}
                onClick={() => {
                    setEntry({
                        id,
                        data,
                    })
                }}
            >
                <Stack flex={1}>
                    <Typography variant="ps">{data.employer}</Typography>
                    <Typography variant="ps" color={styles.neutrals500}>
                        {data.employmentType} ({start.format('MMM YY')} -{' '}
                        {end === null ? 'Present' : end.format('MMM YY')} •{' '}
                        {dayjs
                            .duration(
                                (end === null ? dayjs() : end).diff(start)
                            )
                            .humanize()}
                        )
                    </Typography>
                </Stack>
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation()
                        // remove()
                        setConfirm(true)
                    }}
                >
                    {/* prettier-ignore */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18 6L6 18M6 6L18 18" stroke="#555555" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/> </svg>
                </IconButton>
            </Stack>
        </>
    )
}

const EditModal = ({
    open,
    entry,
    onClose,
    mutateAsync,
}: {
    open: boolean
    onClose: () => void
    mutateAsync: (e: {
        id: string | undefined
        data: TeacherWorkingHistoryEntry
    }) => Promise<void>
    entry:
        | { id: string; data: TeacherWorkingHistoryEntry }
        | { id: undefined; data?: never }
}) => {
    const { handleSubmit, control, formState, watch, setValue } = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: !!entry.id
            ? {
                  ...entry.data,
                  supervisorEmail: entry.data.supervisorEmail || '',
                  startDate: entry.data.startDate
                      ? dayjs(entry.data.startDate, 'MM-YYYY')
                      : undefined,
                  endDate: entry.data.endDate
                      ? dayjs(entry.data.endDate, 'MM-YYYY')
                      : undefined,
                  currentInRole: typeof entry.data.endDate !== 'string',
              }
            : {
                  role: '',
                  employmentType: undefined,
                  employer: '',
                  startDate: undefined,
                  endDate: undefined,
                  supervisorName: '',
                  supervisorRole: '',
                  supervisorContact: '',
                  supervisorEmail: '',
                  currentInRole: false,
                  notes: '',
              },
    })

    const [place, setPlace] = useState<PlaceType | null>(null)

    const handlePlaceChange = (e: PlaceType | null) => {
        setPlace(e)
        logger.debug(e)

        const place = getLocationSafe(e)
        setValue('employerDetails', place)
    }

    const currentlyInRole = watch('currentInRole')
    const isDirty = formState.isDirty

    const isNew = typeof entry.id !== 'string'
    const onSubmit = (data: FormType) => {
        // logger.debug('isDirty', isDirty)
        // logger.debug('isValid', isValid)

        if (!isNew && !isDirty) {
            // logger.debug('skip submission')
            return onClose()
        }
        // logger.debug('submit')
        return mutateAsync({
            id: entry.id,
            data: {
                ...data,
                supervisorContact: data.supervisorContact.replace('+61 ', ''),
                startDate: data.startDate.format('MM-YYYY'),
                supervisorEmail: data.supervisorEmail || null,
                endDate: data.currentInRole
                    ? null
                    : data.endDate !== undefined
                      ? data.endDate.format('MM-YYYY')
                      : null,
            },
        })
    }

    return (
        <Dialog open={open} maxWidth={false}>
            <DialogAppBar handleClose={onClose} />
            <DialogContent
                sx={{
                    p: 3,
                    pt: 1,
                    width: 600,
                }}
            >
                <Stack>
                    <Typography variant="h5" mb={5}>
                        {isNew ? 'Add' : 'Update'} Working History with Children
                    </Typography>

                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        style={{ width: '100%' }}
                    >
                        <Stack gap={5}>
                            <Stack gap={1.5}>
                                <FormInputText
                                    control={control}
                                    name="role"
                                    label={'Role'}
                                    sx={UserRegSx}
                                />
                                <FormSelect
                                    control={control}
                                    name="employmentType"
                                    options={workingHistoryEmploymentTypeOptions.options.map(
                                        (el) => ({
                                            label: el,
                                            value: el,
                                        })
                                    )}
                                    props={{
                                        label: 'Employment Type',
                                    }}
                                    controlProps={{
                                        sx: UserRegSx,
                                    }}
                                />
                                <Controller
                                    control={control}
                                    name="employer"
                                    render={(props) => (
                                        <GoogleMapsTextFieldV2
                                            inputValue={props.field.value || ''}
                                            setInputValue={props.field.onChange}
                                            error={!!props.fieldState.error}
                                            helperText={
                                                props.fieldState.error?.message
                                            }
                                            typesQuery={['establishment']}
                                            value={place}
                                            setValue={handlePlaceChange}
                                            label="Company or organisation"
                                            inputprops={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Stack>

                            <Stack gap={1.5}>
                                <Stack gap={1}>
                                    <Typography>Start Date:</Typography>
                                    <FormDatePicker_v2
                                        control={control}
                                        name="startDate"
                                        datePickerProps={{
                                            format: 'MM-YYYY',
                                            sx: UserRegSx,
                                            views: ['year', 'month'],
                                            maxDate: dayjs(),
                                        }}
                                    />
                                </Stack>
                                <Stack gap={1}>
                                    <Typography>End Date:</Typography>
                                    <FormDatePicker_v2
                                        control={control}
                                        name="endDate"
                                        datePickerProps={{
                                            format: 'MM-YYYY',
                                            disabled: currentlyInRole,
                                            sx: UserRegSx,
                                            views: ['year', 'month'],
                                            maxDate: dayjs(),
                                        }}
                                    />
                                </Stack>
                                <Controller
                                    control={control}
                                    name="currentInRole"
                                    render={(props) => (
                                        <FormControlLabel
                                            label="I am currently in this role"
                                            checked={props.field.value || false}
                                            onChange={props.field.onChange}
                                            control={
                                                // prettier-ignore
                                                <Checkbox
                                                    icon={<svg width="24" height="24" viewBox="0 0 24 24" style={{overflow: 'visible'}} fill="none" xmlns="http://www.w3.org/2000/svg"> <g clipPath="url(#clip0_47_8176)"> <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A3A3A3"/> </g> <defs> <clipPath id="clip0_47_8176"> <rect width="24" height="24" fill="white"/> </clipPath> </defs> </svg> }
                                                    checkedIcon={ <svg width="24" height="24" viewBox="0 0 24 24" style={{overflow: 'visible'}} fill="none" xmlns="http://www.w3.org/2000/svg" > <g clipPath="url(#clip0_47_8756)"> <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#60C1A3" /> <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#60C1A3" /> <path d="M18.6666 7L9.49992 16.1667L5.33325 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> </g> <defs> <clipPath id="clip0_47_8756"> <rect width="24" height="24" fill="white" /> </clipPath> </defs> </svg> }
                                                />
                                            }
                                        />
                                    )}
                                />
                            </Stack>

                            <Stack gap={1.5}>
                                <Typography>Supervisor or referee</Typography>
                                <FormInputText
                                    control={control}
                                    name="supervisorName"
                                    label="Name"
                                    sx={UserRegSx}
                                />
                                <FormInputText
                                    control={control}
                                    name="supervisorRole"
                                    label="Role"
                                    sx={UserRegSx}
                                />
                                <Controller
                                    control={control}
                                    name="supervisorContact"
                                    render={(props) => (
                                        <MuiTelInput
                                            value={props.field.value}
                                            onChange={props.field.onChange}
                                            label="Contact"
                                            onlyCountries={['AU']}
                                            sx={UserRegSx}
                                            error={!!props.fieldState.error}
                                            helperText={
                                                props.fieldState.error?.message
                                            }
                                            disableDropdown
                                            forceCallingCode
                                            defaultCountry="AU"
                                        />
                                    )}
                                />
                                {/* <FormInputContact
                                    sx={UserRegSx}
                                    mask=
                                /> */}
                                <FormInputText
                                    control={control}
                                    name="supervisorEmail"
                                    label={'(Optional) Email'}
                                    sx={UserRegSx}
                                />
                            </Stack>

                            <Stack gap={1.5}>
                                <Typography>(Optional) Note</Typography>
                                <FormInputText
                                    control={control}
                                    name="notes"
                                    label={null}
                                    placeholder="You can leave any notes here..."
                                    multiline
                                    rows={3}
                                    sx={UserRegSx}
                                />
                            </Stack>

                            <PrimaryButton
                                type="submit"
                                sx={{ height: 52 }}
                                loading={formState.isSubmitting}
                            >
                                Save experience
                            </PrimaryButton>
                        </Stack>
                    </form>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default WorkingHistoryComponent
